import { FC, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button } from "../../common/ButtonV2/Button";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateRequestFormDto, CreateRequestResponseDto } from "../types/dto";
import { useRevealPostApi } from "../../../api/hooks/useRevealPostApi";
import { useAppContext } from "../../../contexts/AppContext";
import { UNDERDOTS_FORM } from "../constants";
import { useReCaptcha } from "../hooks/useReCaptcha";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { HubspotFormHack } from "../../common/HubspotHack/HubspotHack";
import { useContactContext } from "../../../contexts/ContactContext";
import { useTitle } from "../../../utils/useTitle";
import { useFavicon } from "../../../hooks/useFavicon";
import { FormBlock } from "../../form/FormBlock";
import { underdotsPublicSchema } from "../types/schema";

type UnderdotsRequestProps = {};

const UNDERDOTS_PAGE_TITLE = "Underdots Processing Request - Reveal";
const UNDERDOTS_FAVICON = "/underdots-favicon.png";

const UnderdotsRequest: FC<UnderdotsRequestProps> = () => {
  const { profileAsync } = useAppContext();
  const { handleReCaptchaVerify } = useReCaptcha();

  useTitle(UNDERDOTS_PAGE_TITLE);
  useFavicon(UNDERDOTS_FAVICON);

  const {
    post,
    loading,
    error: apiError,
    response: submitResponse
  } = useRevealPostApi<CreateRequestFormDto, CreateRequestResponseDto>(
    "/underdots/requests",
    {
      defaultErrorMessage: "Error submitting request, please try again later",
      allowUnauthenticated: true
    }
  );

  const formSchema = yup.object().shape(underdotsPublicSchema);

  const {
    register,
    handleSubmit,
    formState: { errors: formValidationErrors },
    setValue
  } = useForm<CreateRequestFormDto>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: profileAsync.data?.user.emailAddress
    }
  });

  useEffect(() => {
    if (profileAsync.data) {
      setValue("email", profileAsync.data.user.emailAddress);
    }
  }, [profileAsync.data, setValue]);

  const onSubmit: SubmitHandler<CreateRequestFormDto> = async (formData) => {
    const token = await handleReCaptchaVerify();

    try {
      // Trigger linkedin conversion event
      (window as any).lintrk("track", { conversion_id: 14444892 });
    } catch (e) {
      console.error("error triggering linkedin tracking event", e);
    }

    post({ ...formData, recaptchaClientToken: token });
  };

  const { openContactModal } = useContactContext();

  return (
    <form
      className="flex flex-col"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="flex flex-col gap-4">
        {UNDERDOTS_FORM.map((formSection) => {
          return (
            <FormBlock
              formSection={formSection}
              key={formSection.title}
              errors={formValidationErrors}
              register={register}
            />
          );
        })}

        {submitResponse && !apiError && (
          <div className="flex gap-4 items-start p-8 rounded-lg bg-gray-900 shadow-lg">
            <CheckCircleIcon className="w-16 h-16 fill-green-500 -mt-2" />
            <div className="flex flex-col">
              <h2 className="text-xl text-white font-bold mb-1">
                Thanks for submitting your Underdots information
              </h2>
              <p className="text-white reveal-font-body mb-4">
                You can expect to hear from us soon! We will email your quote to{" "}
                <span className="text-underdotsGreen-500">
                  {submitResponse.email}
                </span>
                . Your request ID is {submitResponse.requestId}
              </p>
              <p className="text-white reveal-font-body">
                In the meantime, feel free to{" "}
                <span
                  className="text-underdotsGreen-500 underline underline-offset-4 cursor-pointer"
                  onClick={() => openContactModal()}
                >
                  contact us{" "}
                </span>
                with any queries.
              </p>
            </div>
          </div>
        )}
        {Object.keys(formValidationErrors).length > 0 && (
          <div className="flex flex-col text-red-900 reveal-font-body bg-red-200 rounded px-8 py-4">
            <p>
              A form field has been entered incorrectly, please review each of
              your answers and try to submit the form again.
            </p>
          </div>
        )}
        {apiError && (
          <div className="flex flex-col text-red-900 bg-red-200 rounded px-8 py-4">
            <h3 className="reveal-font-title">Unable to submit form</h3>
            <p className="mt-1 reveal-font-body">
              We weren't able to process your form submission, please try to
              submit the form again. If it continues, please{" "}
              <span
                className="underline underline-offset-4 cursor-pointer"
                onClick={() => openContactModal()}
              >
                contact us{" "}
              </span>
              and we can help.
            </p>
          </div>
        )}

        <HubspotFormHack />

        <div className="flex justify-end items-center gap-5 flex-col md:flex-row">
          <p
            className="reveal-font-body text-gray-500 text-sm 
        "
          >
            By submitting this form you agree to the{" "}
            <a
              href="https://www.reveal.nz/terms-and-conditions"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4"
            >
              terms and conditions.
            </a>
          </p>
          <Button
            id="underdots-request-submit-button"
            color="underdots"
            variant="filled"
            size="regular"
            mode="light"
            type="submit"
            loading={loading}
            disabled={!!submitResponse}
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UnderdotsRequest;
